// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  AddRounded,
  CalendarTodayOutlined,
  RefreshRounded,
} from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { ChargeAddForm } from '@/project/components/charge-add-form';
import { ChargeBillDateGroup } from '@/project/components/charge-bill-date-group';
import { sumChargeTotals } from '@/project/utilities/charge-amounts';
import { chargeDateKey } from '@/project/utilities/charge-date';
import { computeChargeDisplayLabels } from '@/project/utilities/decorate-charge-label';
import { ChargeFormValue } from '@/project/utilities/generate-charges';
import { pluralize } from '@/utilities/pluralize';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

interface Props {
  charges: ChargeFormValue[];
  isLoading: boolean;
  pricingMode: string;
  lineItemQuantity: number;
  currencyCode: IsoCurrencyCode;
  isFrequencyPairValid: boolean;
  /**
   * Non-null when generation must be blocked, with a human-readable reason
   * surfaced as a tooltip. Milestone plans use this to block until phases
   * sum to 100% / totalOverride; periodic plans pass null.
   */
  generateBlockedReason: string | null;
  /**
   * When true, charge-list copy treats bill dates as user-entered expected
   * dates ("Expected bill date"), not cron-driven schedule dates. Periodic
   * plans pass false.
   */
  isMilestoneMode: boolean;
  // Deep-link target. The matching row scrolls into view and briefly
  // highlights once the charges have loaded. Cleared via
  // onFocusChargeComplete after the highlight finishes so a refresh does not
  // re-trigger it.
  focusChargeKey?: string;
  onFocusChargeComplete?: () => void;
  onGenerate: () => void;
  onEditingChange: (isEditing: boolean) => void;
  onUpdateCharge: (index: number, updated: ChargeFormValue) => void;
  onDeleteCharge: (index: number) => void;
  onSplitCharge: (index: number) => void;
  onAddCharge: (charge: ChargeFormValue) => void;
}

export function BillingPlanChargeListSection(props: Props) {
  const hasCharges = props.charges.length > 0;
  // Per the conceptual model, regenerate replaces every non-INVOICED
  // charge — INVOICED is the only locked status. CANCELLED rows are
  // dropped or rewritten by a regenerate just like PLANNED / BILLABLE.
  const hasImmutableCharges = props.charges.some(
    (c) => c.status === 'INVOICED'
  );
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [editingChargeIndex, setEditingChargeIndex] = useState<number | null>(
    null
  );
  const [isAddingCharge, setIsAddingCharge] = useState(false);

  const chargeTotal = useMemo(
    () => sumChargeTotals(props.charges),
    [props.charges]
  );

  const billDateCount = useMemo(() => {
    const dates = new Set(
      props.charges
        .map((c) => chargeDateKey(c.expectedBillableDate))
        .filter((d): d is string => d !== null)
    );
    return dates.size;
  }, [props.charges]);

  const grouped = useMemo(() => {
    // Display labels are derived from sibling grouping (same period +
    // same base label); labels stored on the charge row stay canonical.
    // See decorate-charge-label for the legacy-suffix handling.
    const displayLabels = computeChargeDisplayLabels(props.charges);
    const groups = new Map<
      string,
      { charge: ChargeFormValue; index: number; displayLabel: string }[]
    >();
    for (let i = 0; i < props.charges.length; i++) {
      const charge = props.charges[i];
      const key = chargeDateKey(charge.expectedBillableDate) ?? 'Unscheduled';
      const group = groups.get(key) ?? [];
      group.push({ charge, index: i, displayLabel: displayLabels[i] });
      groups.set(key, group);
    }
    return groups;
  }, [props.charges]);

  const hasModifiedCharges = props.charges.some((c) => c.isCustomModified);
  const onGenerate = props.onGenerate;
  const onEditingChange = props.onEditingChange;

  const handleRegenerateClick = useCallback(() => {
    if (hasCharges && (hasModifiedCharges || hasImmutableCharges)) {
      setShowRegenerateConfirm(true);
    } else {
      onGenerate();
    }
  }, [hasCharges, hasModifiedCharges, hasImmutableCharges, onGenerate]);

  const handleConfirmRegenerate = useCallback(() => {
    onGenerate();
    setShowRegenerateConfirm(false);
  }, [onGenerate]);

  const handleStartEdit = useCallback(
    (index: number) => {
      setEditingChargeIndex(index);
      onEditingChange(true);
    },
    [onEditingChange]
  );

  const handleStopEdit = useCallback(() => {
    setEditingChargeIndex(null);
    onEditingChange(false);
  }, [onEditingChange]);

  if (props.isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
          Charges
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            py: 3,
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {/* Warnings */}
      {showRegenerateConfirm && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          <Typography variant="body2">
            {hasImmutableCharges
              ? 'Regenerating will replace all editable charges. Invoiced charges will be preserved'
              : 'The charge schedule has been manually modified. Regenerating will discard all custom changes and create a new default schedule'}
          </Typography>
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
            <Button
              color="error"
              variant="contained"
              onClick={handleConfirmRegenerate}
              sx={{ textTransform: 'none' }}
            >
              Regenerate anyway
            </Button>
            <Button
              onClick={() => setShowRegenerateConfirm(false)}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
          </Box>
        </Alert>
      )}

      {hasImmutableCharges && !showRegenerateConfirm && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
          <Typography variant="body2">
            Regeneration is disabled while any charge is invoiced
          </Typography>
        </Alert>
      )}

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant="subtitle2">Charges</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {hasCharges && (
            <Tooltip
              arrow
              title={
                editingChargeIndex !== null || isAddingCharge
                  ? 'Save or cancel the open charge edit first'
                  : ''
              }
            >
              <span>
                <Button
                  startIcon={<AddRounded />}
                  disabled={editingChargeIndex !== null || isAddingCharge}
                  onClick={() => {
                    setIsAddingCharge(true);
                    onEditingChange(true);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Add charge manually
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip
            arrow
            title={
              editingChargeIndex !== null || isAddingCharge
                ? 'Save or cancel the open charge edit first'
                : (props.generateBlockedReason ?? '')
            }
          >
            <span>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<RefreshRounded />}
                onClick={hasCharges ? handleRegenerateClick : onGenerate}
                disabled={
                  editingChargeIndex !== null ||
                  isAddingCharge ||
                  !props.isFrequencyPairValid ||
                  props.generateBlockedReason !== null
                }
                sx={{ textTransform: 'none' }}
              >
                {hasCharges
                  ? 'Regenerate full schedule'
                  : 'Generate full schedule'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {!hasCharges ? (
        <Box
          sx={{
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <CalendarTodayOutlined
            sx={{ fontSize: '2rem', color: 'text.secondary' }}
          />
          <Typography variant="body2" color="text.secondary">
            No charges generated
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure your frequencies above and click "Generate charges"
          </Typography>
        </Box>
      ) : (
        <>
          <Typography
            variant="body2"
            color="text.secondary"
            display="block"
            sx={{ mb: 1.5 }}
          >
            {formatCurrencyValue({
              amount: chargeTotal,
              isoCode: props.currencyCode,
            })}{' '}
            total
            {' \u00B7 '}
            {pluralize(billDateCount, {
              singular: props.isMilestoneMode
                ? 'expected bill date'
                : 'bill date',
            })}
            {' \u00B7 '}
            {pluralize(props.charges.length, { singular: 'charge' })}
          </Typography>

          {isAddingCharge && (
            <ChargeAddForm
              pricingMode={props.pricingMode}
              lineItemQuantity={props.lineItemQuantity}
              currencyCode={props.currencyCode}
              isMilestone={props.isMilestoneMode}
              onSave={(charge) => {
                props.onAddCharge(charge);
                setIsAddingCharge(false);
                onEditingChange(false);
              }}
              onCancel={() => {
                setIsAddingCharge(false);
                onEditingChange(false);
              }}
            />
          )}

          <Box sx={{ mt: 1 }}>
            {Array.from(grouped.entries()).map(
              ([dateKey, groupCharges], groupIndex) => (
                <Box key={dateKey}>
                  {groupIndex > 0 && <Divider sx={{ my: 1 }} />}
                  <ChargeBillDateGroup
                    dateKey={dateKey}
                    charges={groupCharges}
                    currencyCode={props.currencyCode}
                    isMilestoneMode={props.isMilestoneMode}
                    editingChargeIndex={
                      isAddingCharge ? -1 : editingChargeIndex
                    }
                    focusChargeKey={props.focusChargeKey}
                    onFocusChargeComplete={props.onFocusChargeComplete}
                    onStartEdit={handleStartEdit}
                    onStopEdit={handleStopEdit}
                    onUpdateCharge={props.onUpdateCharge}
                    onDeleteCharge={props.onDeleteCharge}
                    onSplitCharge={props.onSplitCharge}
                  />
                </Box>
              )
            )}
          </Box>
        </>
      )}
    </Paper>
  );
}
